import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, tap, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PhotoViewModel } from './models/photo-view.model';
import { InspectionTypeSamplePhotoUploadRequest } from './models/inspection-type-sample-photo-upload-request.model';
import { InspectionItemPhotoUploadRequest } from './models/inspection-item-photo-upload-request.model';
import { AnomalyPhotoUploadRequest } from './models/anomaly-photo-upload-request';
import { UpdatePhotoReferencesRequest } from './models/update-photo-references-request';
import { OfflineChangesService } from 'projects/phdsoft/common/src/lib/shared/services/offline-changes.service';

@Injectable({
  providedIn: 'root'
})
export class PhotoService {

  private photoDeletedSubject = new BehaviorSubject<string | null>(null);
  public photoDeleted$ = this.photoDeletedSubject.asObservable();

  constructor(
    private http: HttpClient,
    private readonly offlineChangesService: OfflineChangesService
  ) { }

  /**
   * Add a photo for an inspection type sample/example
   */
  addInspectionTypeSamplePhoto(request: InspectionTypeSamplePhotoUploadRequest, file: File): Observable<PhotoViewModel> {
    const formData = new FormData();
    formData.append('formFile', file);
    formData.append('request', JSON.stringify(request));

    return this.http.post<PhotoViewModel>(
      environment.API_MEDIA + 'Photo/InspectionTypeSample',
      formData
    );
  }

  /**
   * Get a photo by ID
   * @param id The photo ID
   * @returns Observable with the photo view model
   */
  getPhoto(id: string): Observable<PhotoViewModel> {
    return this.http.get<PhotoViewModel>(
      environment.API_MEDIA + `Photo/${id}`
    );
  }

  /**
   * Delete a photo by ID
   * @param id The photo ID
   * @returns Observable with void
   */
  deletePhoto(id: string): Observable<void> {
    if (window.navigator.onLine) {
      return this.http.delete<void>(
        environment.API_MEDIA + `Photo/${id}`
      ).pipe(
        tap(() => {
            this.photoDeletedSubject.next(id);
          })
        );
    }
    else {
      this.offlineChangesService.addItem("photo", environment.API_MEDIA + `Photo/${id}`, "delete", null)
      this.photoDeletedSubject.next(id);
      return of()
    }
  }

  /**
   * Get photos for an anomaly
   * @param anomalyId The anomaly ID
   * @param assetId The asset ID
   * @param inspectionItemId The inspection item ID
   *    (optional but recommanded in case the photos are not linked to the anomaly by some mistake/error)
   * @returns Observable with an array of photo view models
   */
  getAnomalyPhotos(anomalyId: string, assetId: string, inspectionItemId: string): Observable<PhotoViewModel[]> {
    return this.http.get<PhotoViewModel[]>(
      environment.API_MEDIA + `Photo/AnomalyPhotos?anomalyId=${anomalyId}&assetId=${assetId}&inspectionItemId=${inspectionItemId}`
    );
  }

  /**
   * Get photos for an inspection type
   * @param inspectionTypeId The inspection type ID
   * @returns Observable with an array of photo view models
   */
  getInspectionTypePhotos(inspectionTypeId: string): Observable<PhotoViewModel[]> {
    return this.http.get<PhotoViewModel[]>(
      environment.API_MEDIA + `Photo/InspectionTypePhotos?inspectionTypeId=${inspectionTypeId}`
    );
  }

  /**
   * Get photos for an inspection element
   * @param inspectionElementId The inspection element ID
   * @param assetId The asset ID
   * @returns Observable with an array of photo view models
   */
  getInspectionElementPhotos(inspectionElementId: string, assetId: string): Observable<PhotoViewModel[]> {
    return this.http.get<PhotoViewModel[]>(
      environment.API_MEDIA + `Photo/InspectionElementPhotos?inspectionElementId=${inspectionElementId}&assetId=${assetId}`
    );
  }

  /**
   * Add a photo for an inspection item
   */
  addInspectionItemPhoto(request: InspectionItemPhotoUploadRequest, file: File): Observable<PhotoViewModel> {
    const formData = new FormData();
    formData.append('formFile', file);
    formData.append('request', JSON.stringify(request));

    return this.http.post<PhotoViewModel>(
      environment.API_MEDIA + 'Photo/InspectionItem',
      formData
    );
  }

  addAnomalyPhoto(request: AnomalyPhotoUploadRequest, file: File): Observable<PhotoViewModel> {
    const formData = new FormData();
    formData.append('formFile', file);
    formData.append('request', JSON.stringify(request));

    return this.http.post<PhotoViewModel>(
      environment.API_MEDIA + 'Photo/Anomaly',
      formData
    );
  }

  updatePhotoDescription(photoId: string, description: string): Observable<void> {
    return this.http.patch<void>(
      environment.API_MEDIA + `Photo/${photoId}/description`,
      { Description: description }
    );
  }

  updatePhotoReferences(photoId: string, request: UpdatePhotoReferencesRequest): Observable<void> {
    const url = environment.API_MEDIA + `Photo/${photoId}/references`
    if (window.navigator.onLine) {
      return this.http.put<void>(
        url,
        request
      );
    }
    else {
      this.offlineChangesService.addItem("photo", url, "put", request)
      return of()
    }
  }
}
